import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Sparkline from '../components/Sparkline';
import { fetchAllMps, fetchMeta, PARTY_COLORS, fmtMoney, fmtMoneyShort } from '../api';
import { Search, ArrowUpRight } from 'lucide-react';

const PARTIES = ['ALL', 'LIB', 'CPC', 'NDP', 'BQ', 'GPC', 'IND'];

export default function LatestQuarter() {
  const [q, setQ] = useState('');
  const [party, setParty] = useState('ALL');
  const [mps, setMps] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetchMeta().catch(() => null),
      fetchAllMps({ limit: 400 }).catch(() => ({ mps: [] })),
    ]).then(([m, all]) => {
      setMeta(m);
      setMps(all.mps || []);
      setLoading(false);
    });
  }, []);

  const filtered = useMemo(() => {
    return mps.filter(m => {
      if (party !== 'ALL' && m.party !== party) return false;
      if (q && !m.name.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [q, party, mps]);

  const gross = filtered.reduce((s, m) => s + (m.total || 0), 0);
  const max = filtered.reduce((m, x) => Math.max(m, x.total || 0), 0) || 1;

  return (
    <div className="min-h-screen bg-cream-100 text-ink-700">
      <Header />

      {/* Page header banner */}
      <section className="bg-ink-800 text-cream-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
          <div className="kicker text-copper-500 mb-4">MP expenses report · {meta?.latest_quarter || ''}</div>
          <h1 className="font-serif text-[56px] md:text-[68px] leading-[0.98] tracking-tight">
            Latest quarter <span className="italic font-light text-copper-500">ranked.</span>
          </h1>
          <p className="mt-6 text-[16px] text-cream-100/75 max-w-2xl leading-relaxed">
            Every parliamentarian, ranked by gross claimed. Figures from the House of Commons of Canada
            Members' Expenditures Report, published quarterly under the Board of Internal Economy.
          </p>
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-6 lg:px-12 -mt-10">
        {/* Stats row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          <StatTile label="Gross claimed (filtered)" value={fmtMoneyShort(gross)} />
          <StatTile label="Parliamentarians" value={filtered.length} />
          <StatTile label="Total seats" value={meta?.total_mps || 343} />
          <StatTile label="Quarter" value={meta?.latest_quarter || '—'} small />
        </div>

        <div className="flex flex-col md:flex-row gap-4 md:items-center md:justify-between mb-8">
          <div className="relative md:w-96">
            <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-500" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Filter by name"
              className="w-full pl-10 pr-3 py-2.5 rounded-sm border border-ink-300 bg-cream-50 text-[14px] text-ink-700 placeholder-ink-500 focus:outline-none focus:border-copper-500 focus:ring-2 focus:ring-copper-500/15"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {PARTIES.map((p) => (
              <button
                key={p}
                onClick={() => setParty(p)}
                className={`px-3.5 py-1.5 text-[12px] uppercase tracking-wider rounded-sm border transition ${
                  party === p
                    ? 'border-ink-800 bg-ink-800 text-cream-100'
                    : 'border-ink-300 bg-cream-50 text-ink-700 hover:border-copper-500 hover:text-copper-600'
                }`}
              >
                {p === 'ALL' ? 'All parties' : p}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-cream-50 border border-ink-300/60 rounded-sm overflow-hidden mb-16">
          <div className="hidden md:grid grid-cols-[60px_1.6fr_1fr_1.4fr_1.2fr_1fr] kicker text-ink-500 px-6 py-4 border-b border-ink-300/50 bg-cream-100/40">
            <div>#</div>
            <div>Parliamentarian</div>
            <div>Party · Prov</div>
            <div>Largest line</div>
            <div>Trend</div>
            <div className="text-right">Claimed</div>
          </div>

          {loading ? (
            <div className="py-32 text-center text-ink-500">Loading live data from House of Commons…</div>
          ) : filtered.length === 0 ? (
            <div className="py-24 text-center text-ink-500">No parliamentarians match those filters.</div>
          ) : filtered.map((m, idx) => (
            <Link
              key={m.slug + idx}
              to={`/mp/${m.slug}`}
              className="group relative md:grid md:grid-cols-[60px_1.6fr_1fr_1.4fr_1.2fr_1fr] flex flex-col md:items-center px-6 py-4 border-b border-ink-300/40 last:border-0 hover:bg-cream-100/60 transition"
            >
              <div className="absolute inset-y-0 left-0 bg-copper-50/0 group-hover:bg-copper-50/40 transition" style={{ width: `${((m.total || 0) / max) * 100}%` }} />
              <div className="relative text-[13px] text-ink-500 tabular">{String(idx + 1).padStart(3, '0')}</div>
              <div className="relative">
                <div className="text-[15px] text-ink-800 font-medium group-hover:text-copper-600 transition inline-flex items-center gap-1.5">
                  {m.name}
                  <ArrowUpRight size={12} className="opacity-0 group-hover:opacity-100 transition" />
                </div>
                {m.constituency && <div className="text-[11px] text-ink-500 mt-0.5">{m.constituency}</div>}
              </div>
              <div className="relative text-[12px] tracking-wider uppercase text-ink-700 mt-1 md:mt-0 inline-flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 rounded-full" style={{ background: PARTY_COLORS[m.party] }} />
                {m.party} <span className="text-ink-300">·</span> {m.province || '—'}
              </div>
              <div className="relative text-[13px] text-ink-700 mt-1 md:mt-0">
                {m.line} <span className="text-ink-500 tabular">· {fmtMoney(m.line_amount)}</span>
              </div>
              <div className="relative mt-2 md:mt-0">
                {(m.trend && m.trend.length) ? <Sparkline data={m.trend.filter(v => v > 0)} width={120} height={32} /> : <span className="text-ink-300 text-[12px]">—</span>}
              </div>
              <div className="relative text-left md:text-right mt-2 md:mt-0">
                <div className="font-serif text-[18px] text-ink-800 tabular">{fmtMoney(m.total)}</div>
                <div className="text-[11px] text-ink-500">disc. {fmtMoney(m.discretionary)}</div>
              </div>
            </Link>
          ))}
        </div>
      </main>
      <Footer />
    </div>
  );
}

function StatTile({ label, value, small }) {
  return (
    <div className="bg-cream-50 border border-ink-300/60 rounded-sm p-5">
      <div className="kicker text-ink-500 mb-2">{label}</div>
      <div className={`font-serif text-ink-800 tabular tracking-tight ${small ? 'text-[18px]' : 'text-[26px]'}`}>{value}</div>
    </div>
  );
}
